/*--------------Libraries---------------------*/
import ReactDOM from 'react-dom/client';
import reportWebVitals from './reportWebVitals';
import { store, StoreContext } from './app/stores/store';
import { RouterProvider } from 'react-router-dom';
import router from './app/router/Index';
import ThemeCustomization from './app/themes';
import ToasterContainer from './app/shared/toaster/Index';
import PwaUpdateNotification from './app/shared/components/PwaUpdateNotification';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorBoundaryFallback from './app/shared/common/ErrorBoundary';
import type { ErrorInfo } from 'react';
import { initKeycloak } from './config/keycloak';

// style.scss
import './assets/style.css';

// scroll bar
import 'simplebar-react/dist/simplebar.min.css';

// leaflet maps
import 'leaflet/dist/leaflet.css';

// apex-chart
import './assets/third-party/apex-chart.css';
import './assets/third-party/react-table.css';

// google-fonts
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/700.css';

import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';

import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';

import '@fontsource/public-sans/400.css';
import '@fontsource/public-sans/500.css';
import '@fontsource/public-sans/600.css';
import '@fontsource/public-sans/700.css';

/*-------Global Chunk Load Error Detection-------*/
function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  return (
    error.name === 'ChunkLoadError' ||
    /loading chunk/i.test(error.message) ||
    /chunkloaderror/i.test(error.message) ||
    /failed to fetch dynamically imported module/i.test(error.message) ||
    /importing a module script failed/i.test(error.message) ||
    /failed to fetch/i.test(error.message) ||
    /networkerror/i.test(error.message) ||
    /load failed/i.test(error.message)
  );
}


window.addEventListener('unhandledrejection', (event) => {
  if (isChunkLoadError(event.reason)) {
    console.warn('[ChunkLoadError] Chunk failed to load (offline/slow network):', event.reason?.message);
    event.preventDefault();
  }
});

/*-------React Error Boundary handler-------*/
function handleReactError(error: unknown, errorInfo: ErrorInfo): void {
  console.error('[React Error Boundary]', error, errorInfo);
}

/*-------Render App-------*/
const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

initKeycloak(() => {
  root.render(
    <ErrorBoundary
      FallbackComponent={ErrorBoundaryFallback}
      onError={handleReactError}
    >
      <ThemeCustomization>
        <StoreContext.Provider value={store}>
          <ToasterContainer />
          <PwaUpdateNotification />
          <RouterProvider router={router} />
        </StoreContext.Provider>
      </ThemeCustomization>
    </ErrorBoundary>
  );
});

reportWebVitals();
serviceWorkerRegistration.register();